import { useMemo } from 'react';
import { useCallbackOne as useCallback } from 'use-memo-one';
import gql from 'graphql-tag';
import { useMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ContextId, type ExtensionId } from '@atlassian/forge-ui-types';

import {
	type GQLInvokeExtensionEntrypoint,
	type GQLInvokeExtensionInput,
	type GQLInvokeExtensionPayload,
	type GQLInvokeExtensionResponse,
} from '../graphql/types';
import { createAtlAttributionHeader } from '../../utils';

export interface MutationVariables {
	input: GQLInvokeExtensionInput;
}

export interface MutationData {
	invokeExtension: GQLInvokeExtensionResponse;
}

export const invokeExtensionMutation = gql`
	mutation forge_ui_invokeExtension($input: InvokeExtensionInput!) {
		invokeExtension(input: $input) {
			success
			response {
				body
			}
			contextToken {
				jwt
				expiresAt
			}
			errors {
				message
				extensions {
					errorType
					statusCode
					... on InvokeExtensionPayloadErrorExtension {
						fields {
							authInfoUrl
						}
					}
				}
			}
		}
	}
`;

export const useInvokeExtension: Function = (
	contextIds: ContextId[],
	extensionId: ExtensionId,
	options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const [mutationFunction] = useMutation<MutationData, MutationVariables>(
		invokeExtensionMutation,
		options,
	);

	const headers = useMemo(() => {
		const initHeaders: Record<string, string> = {};
		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
		if (fg('forge-ui-include-atl-attribution-header')) {
			initHeaders['atl-attribution'] = createAtlAttributionHeader(contextIds.sort().join());
		}
		return initHeaders;
	}, [contextIds]);

	return useCallback(
		(payload: GQLInvokeExtensionPayload, entryPoint: GQLInvokeExtensionEntrypoint = 'resolver') =>
			mutationFunction({
				variables: {
					input: {
						contextIds,
						extensionId,
						payload,
						entryPoint,
					},
				},
				context: {
					headers: headers,
				},
			}),
		[contextIds, extensionId, mutationFunction, headers],
	);
};
